/* eslint-disable no-underscore-dangle */

'use strict';

define('vb/private/services/catalogHandler',['vb/private/utils',
  'vb/private/constants',
  'vb/private/services/serviceConstants',
  'vb/private/log',
  'vb/private/pathHandler',
  'vb/private/services/uriTemplate',
  'vbc/private/constants',
  'vb/private/services/endpoint',
  'vb/private/services/catalogRegistry',
  'vb/private/services/readers/openApiObjectFactory',
  'vb/private/services/readers/openApiUtils',
  'urijs/URI'],
(Utils, Constants, ServiceConstants, Log, PathHandler, UriTemplate, CommonConstants, Endpoint, CatalogRegistry,
  OpenApiObjectFactory, OpenApiUtils, URI) => {
  const logger = Log.getLogger('/vb/private/services/catalogHandler');


  // the 'base' path for the values in the config object for token replacement (ex. services.catalog.<etc>).
  const TOKEN_VARIABLES_PATH = ['services', 'catalog'];
  const TOKEN_COMMON_VARIABLES_PATH = ['services', 'catalog', 'common'];

  /**
   * ideally, we can keep the dependency between ProtocolRegistry and CatalogHandler one-way, and not have
   * this depend on ProtocolRegistry use.
   *
   * But with the introduction of Endpoint creation, we need one; for now, do NOT support metadata
   * "paths" definitions that reference registered protocols.
   * The should be path fragments anyway; it doesn't make sense for the "paths" object to need to reference the catalog.
   * this represents a 'minimal' protocolRegistry duck-type for getting the endpoint info
   * for the "services" fragment.
   */
  const stubRegistry = {
    // return the same default object that the protocolRegistry would return, if we were using the real one
    getResolvedInfoOrDefault: (url, namespace) => Promise.resolve({
      url,
      namespace,
      extensions: {
        [ServiceConstants.ExtensionTypes.SERVICES]: {},
        [ServiceConstants.ExtensionTypes.BACKENDS]: {},
      },
      chain: [],
    }),
  };


  /**
   * the CatalogHandler uses the catalog.json for adding extensions to a 'static' service definition (swagger, openapi),
   * and also defining server paths using variable templates.
   *
   * For OpenAPI 3 support, we have one service definition for both design-time and runtime, and the server
   * (ideally) does not inject extensions into service defs that are based on the environment.
   * The catalog.json allows app developers to add application-specific extensions to the service defs,
   * and also specify values for server path template substitutions.
   *
   * The catalog cannot override anything in the service definition; it is only meant to be additive.
   *
   * there are two sections:
   *   "services":
   *     applies to the entire service; defines security for both service metadata and data endpoints
   *
   *   "backends":
   *      defines additional headers/extension info for data endpoints.
   *      backends are meant to be re-usable by different services.
   *      backends are ignored for extensions
   */

  // new syntax for services in 19.3.1
  /*
  "services": {
      "service1": {
          "openapi": "3.0",
          "info": {
               "title": "My Cool Service"
          },
          "servers": [
              {
                  "url": "vb-catalog://backends/backend1/"
              },
              {
                  "url": "https://test.notreal/other/",
                  "x-vb": {
                      "headers": {
                          "header4": "header4Value"
                      },
                      "authentication": {
                      },
                      "profiles": [ "test" ]
                  }
              }
          ],
          "paths": {
              "/metadata/openapi3.json": {
                  "get": {
                      "x-vb": {
                          "headers": {
                              "header3": "header3Value"
                          }
                      }
                  }
              }
          }
      }
  }
  */

  class CatalogHandler {
    /**
     * @param config
     *  - 'catalog': optional, path to catalog.json (uses requireJS to load). defaults to '../../services/catalog.json'
     *    currently, this is unused (I think), since the server would not be able to handle alternate locations
     *    for the catalog (the server does some injection if it finds one in the default location).
     *    If you did have a need to define one in an alternate location, it would be configured in the app-flow.json as:
     *      "configuration": {
     *         "catalog": {
     *           "path": "../../myfolder/catalog.json"
     *         }
     *      }
     *
     *  - 'services.catalog.backends/services: optional objects that contain the values for server path tokens
     *
     *  @param profile current profile, defaults to empty string (means 'use the first server').
     *    note that this setting is in the config.profile, but we centralize where that's determined, since
     *    other parts of the services layer also require it.
     *
     * there is a third 'protocol' parameter, but we don't use it, since we only handle one protocol.
     *
     * @param tenantCatalog {object} not used. @see vbExtensionHandler
     * @param catalogRegistry {CatalogRegistry}
     */
    constructor(config, profile, tenantCatalog, catalogRegistry) {
      this.config = config || {};

      if (this.config.catalog && this.config.catalog.path) {
        this.catalogPath = new PathHandler(this.config.catalog.path,
          this.config.relativePath || '', { allowParent: true }).getResolvedPath();
      }
      this.catalogPath = this.catalogPath || CatalogHandler.getDefaultPath();

      this.profile = profile || '';

      this.registry = catalogRegistry;
      // @todo: eventually, we can register the tenant catalog here also, when it exists
      this.registry.register(CatalogRegistry.ROOT_CATALOG, this.catalogPath);
    }

    /**
     *
     * @param config not used
     * @returns true if this handler should be installed
     */
    static shouldInstall(/* config */) {
      // return !!(config && config.catalog);
      return true;
    }

    /**
     * for now, DT assumes a hard-coded path; if the file doesn't exist, this 'plugin' will fail,
     * but protocol resolution will be the default behavior - none.
     * @returns {*}
     */
    static getDefaultPath() {
      // using a static property so we can clear it in unit tests
      if (!CatalogHandler._defaultCatalogPath) {
        CatalogHandler._defaultCatalogPath = Constants.DefaultPaths.CATALOG_JSON;
      }
      return CatalogHandler._defaultCatalogPath;
    }

    /**
     * remove the load Promise, so it can be refreshed.
     */
    dispose() {
      this.loadPromise = null;
      this.registry.dispose();
    }


    /**
     * @param url {string}
     * @param namespace {string}
     * @param parsedInfo {object} from URIjs
     * @return {Promise}
     */
    getResolvedObject(url, namespace, parsedInfo) {
      return this.registry.loadPending()
        .then(() => {
          // vb-catalog://<services/backends>/<id>/rest/of/path....

          const catalogType = parsedInfo.hostname;
          const pathParts = (parsedInfo.path || '').split(Constants.PATH_SEPARATOR);
          const id = pathParts[1] || '';

          return this._resolve(id, namespace, parsedInfo, catalogType);
        });
    }

    /**
     * follows vb-catalog references until the final backend,
     * or rather, no more vb-catalog references, which is defined to necessarily be a backend (currently?).
     *
     * @param id
     * @param namespace
     * @param originalUrlInfo from URI.parse()
     * @param catalogObjectType "backends" or "servers"
     * @returns {Object}
     * @private
     */
    _resolve(id, namespace, originalUrlInfo, catalogObjectType) {
      let catalogObject;
      let currentNamespace = namespace;

      return this._findObjectInNamespaces(id, namespace, catalogObjectType)
        .then((found) => {
          if (found) {
            catalogObject = CatalogHandler._filterCatalogObject(found.catalogObject, catalogObjectType);
            currentNamespace = found.namespace;
            return CatalogHandler.createOpenApiMetadata(id, catalogObject, this.catalogPath);
          }

          const msg = `Catalog object "${id}" for type "${catalogObjectType}" not found in catalog.json `
            + `for namespace ${namespace}`;
          logger.error(msg);

          throw new Error(msg);
        })
        .then((metadata) => {
          if (catalogObject) {
            if (metadata) {
              catalogObject.metadata = metadata;
            }

            const server = this._findServer(catalogObject.servers);

            // followed one level of protocol indirection, but do not do template substitution
            const referencedUrl = CatalogHandler._resolvedUrl(server.url, originalUrlInfo);

            // 'server' (in 'resolved') will contain the chosen server (from 'servers')
            // the url in this object has tokens replaced

            const resolved = this._createResolvedInfo(catalogObject, server, referencedUrl,
              catalogObjectType, id, currentNamespace);

            logger.info('catalog handler, resolving', id, 'to', resolved.url);
            // we still need to merge its extensions (x-vb) with any referenced catalog object.
            return resolved;
          }
          logger.error('No catalog object matching type and id', catalogObjectType, id, 'was found');
          return null;
        });
    }

    /**
     * backends do not directly use namespaces;  the external reference (outside catalog.json) to the service does,
     * but one we are resolving "vb-catalog" urls, we just look
     * - a) first in the catalog in the namespace ofthe external reference
     * - b) then 'base' (if (a) wasn't already 'base'.
     * - c) @todo: implement tenant catalog.
     *
     * find the object type; look first in our namespace, then 'base'
     *
     * (eventually, we will likely syupport 'tenant' also)
     * see: https://confluence.oraclecorp.com/confluence/display/ABCS/
     *     Services+Extension+Dependency+Scenarios#ServicesExtensionDependencyScenarios-Backends.2
     *
     * @param id
     * @param namespace
     * @param type
     * @returns {*}
     * @private
     */
    _findObjectInNamespaces(id, namespace, type) {
      // first, look in the current namespace
      let currentNamespace = namespace;
      return this.registry.get(currentNamespace)
        .then((catalog) => catalog && catalog[type] && catalog[type][id])
        .then((catObject) => {
          if (!catObject && currentNamespace !== Constants.CatalogNamespaces.BASE) {
            // if we didn't find it in our namespace, try base (if we didn't already)
            currentNamespace = Constants.CatalogNamespaces.BASE;

            return this.registry.get(currentNamespace)
              .then((catalog) => catalog && catalog[type] && catalog[type][id]);
          }
          return catObject;
        })
        // @todo: we would try TENANT next, when implemented
        .then((catalogObject) => ({
          catalogObject,
          namespace: currentNamespace, // the namespace it was found in
        }));
    }


    /**
     * remove any 'illegal' properties. protecting against misuse (or confusion).
     *
     * the ‘catalogObject’ is actually a sub-object from the original parsed JSON, so this is doing a shallow copy
     * to be prudent to keep the original, whole, object intact.
     *
     * For SERVICES objects, if its a NEW syntax (contains an 'openapi3' object),
     * just log a warning if this is not using the new 'openapi' "paths" syntax
     *
     * For BACKENDS,
     * We are deleting the properties to prevent them from being using in a merge.
     * “services” and “backends” are nearly identical objects. and when they are merged together,
     * the differences need to be respected.
     *
     * For OLDER-SYNTAX SERVICES objects, there is a difference in how the headers are treated –
     * service and backend headers are kept separate, and the service headers are used ONLY for fetching the metadata.
     *
     * (Note: new syntax service headers are merged with backend headers).
     *
     * Also, backends *should not* define proxyUrls/tokenRelayUrls, so delete them.
     * So, basically:
     * - no, we don’t strictly need to do the shallow copy, and
     * - no, we don’t need to delete the proxyUrls, and hopefully they are never there.
     * @param catalogObject {Object} the services or backends object found in the catalog
     * @param catalogObjectType {string} 'services' or 'backends'
     * @private {Object}
     */
    static _filterCatalogObject(catalogObject, catalogObjectType) {
      let obj = catalogObject;
      if (obj) {
        obj = Object.assign({}, obj);

        if (catalogObjectType === ServiceConstants.ExtensionTypes.BACKENDS) {
          delete obj.proxyUrls;
          delete obj.tokenRelayUrls;
          if (Array.isArray(obj.servers)) {
            obj.servers = obj.servers.map((server) => {
              const extensions = server[ServiceConstants.VB_EXTENSIONS];
              if (extensions && (extensions.proxyUrls || extensions.tokenRelayUrls)) {
                logger.error('service catalog backends should not contain proxyUrls or tokenRelayUrls;',
                  'removing and continuing');
                const newExtensions = Object.assign({}, extensions);
                delete newExtensions.proxyUrls;
                delete newExtensions.tokenRelayUrls;
                return Object.assign({}, server, { [ServiceConstants.VB_EXTENSIONS]: newExtensions });
              }
              return server;
            });
          }
        } else if (catalogObjectType === ServiceConstants.ExtensionTypes.SERVICES) {
          // if we have this attribute, we also mat have a "paths" object, which can be used to specify how to fetch
          // the metadata (swagger/openapi). this is a standard openapi "paths" syntax, but should only
          // have ONE path, and that should have only ONE method (typically a get).
          // that path will be appended to the "servers" object path.
          if (!catalogObject.openapi) {
            logger.warn('deprecated catalog services syntax. headers will not be merged with backends');
          }
        }
      }
      return obj;
    }

    /**
     * A "services" object can optionally use an "openapi" fragment syntax to specify the operation used to
     * get the service metadata (openapi3, swagger2).
     *
     * From that openapi3 fragment, create an Endpoint to fetch the metadata, and some additional properties,
     * to simplify the metadata load.
     *
     * This resolves with this structure (and the caller sets this as the 'metadata' property of the catalog info)
     * {
     *   path?: {string}
     *   query?: {string}
     *   method?: {string} lowercase
     *   extensions?: {Object} the "x-vb" contents
     *   openapi: {string} the version string
     * }
     * @param id catalogObject ID
     * @param catalogObject
     * @param catalogPath
     * @returns {Promise<object>}
     */
    static createOpenApiMetadata(id, catalogObject, catalogPath) {
      // if we have this attribute, we also may have a "paths" object, which can be used to specify how to fetch
      // the metadata (swagger/openapi). this is a standard openapi "paths" syntax, but should only
      // have ONE path, and that should have only ONE method (typically a get).
      // that path will be appended to the "servers" object path.
      return Promise.resolve()
        .then(() => {
          if (!catalogObject) {
            throw new Error(`Catalog object "${id}" not found in catalog.json.`);
          }
          return catalogObject.openapi ? CatalogHandler.getEndpointConfiguration(id, catalogObject, catalogPath) : null;
        })
        .then((endpointConfig) => {
          if (endpointConfig) {
            const pathFrag = new URI(endpointConfig.url);
            const path = pathFrag.path();
            // now that this uses an Endpoint, the query is endcoded, so decode the 'query' to match previous behavior.
            const query = decodeURIComponent(pathFrag.query());

            const method = endpointConfig.method.toLowerCase();
            // @todo: might make sense to include this as an object in the endpoint.getConfiguration?
            const extensionsHeader = endpointConfig.headers[CommonConstants.Headers.VB_INFO_EXTENSION];

            return {
              openapi: catalogObject.openapi,
              path,
              query,
              extensions: extensionsHeader ? JSON.parse(extensionsHeader) : {},
              method,
            };
          }

          if (catalogObject.openapi) {
            // if we didn't have enough metadata to create an endpoint,
            // at least track that it was an openapi3 fragment (the new syntax, for a "services' object)
            return {
              openapi: catalogObject.openapi,
            };
          }
          return null; // this wasn't an openapi3 fragment, the caller should ignore our results
        });
    }

    /**
     * follows the protocol indirection, does not do any token substitution
     *
     * @param serverUrl url from the current "servers" object that is being procesed for the
     *  referenced services/backends obejct
     * @param originalUrlInfo URIjs info for the result of the current expansion; this is what
     * refers to the services/backends whose "servers" url is 'serverUrl'
     *
     *
     * @returns {string}
     * @private
     */
    static _resolvedUrl(serverUrl, originalUrlInfo) {
      // create a new URL by appending the original 'path' part to the found server url
      // can't use URI().segment(), because it will encode the braces in the tokens!
      const serverUri = new URI(serverUrl);
      // get the prefix without the query string; we need to manage those ourselves, to preserve templates (?x={foo})
      const uriPrefix = serverUri.clone().search('').toString();
      const endsWith = uriPrefix.endsWith(Constants.PATH_SEPARATOR);


      // need to remove the service/backend 'id' from the path portion
      const pathNoLeadingSlash = originalUrlInfo.path.startsWith(Constants.PATH_SEPARATOR) ?
        originalUrlInfo.path.substring(1) : originalUrlInfo.path;
      const pathParts = pathNoLeadingSlash.split(Constants.PATH_SEPARATOR);
      pathParts.shift();

      let pathSuffix = pathParts.join(Constants.PATH_SEPARATOR);
      if (pathSuffix.startsWith(Constants.PATH_SEPARATOR)) {
        pathSuffix = pathSuffix.substring(1);
      }

      let newUrl;
      if (pathSuffix) {
        const delim = !endsWith ? Constants.PATH_SEPARATOR : '';
        newUrl = `${uriPrefix}${delim}${pathSuffix}`;
      } else {
        // remove trailing slashes if URI added it
        newUrl = endsWith && (uriPrefix === `${serverUrl}${Constants.PATH_SEPARATOR}`)
          ? uriPrefix.substring(0, uriPrefix.length - 1) : uriPrefix;
      }
      // check if there are query parms;  not using URIjs because we are preserving template strings ("{mytemplate}");
      // template replacement is handled at the end.
      const qparams = [originalUrlInfo.query, serverUri.query()].filter(q => !!q);
      if (qparams.length) {
        newUrl = `${newUrl || ''}?${qparams.join('&')}`;
      }

      return newUrl;
    }

    /**
     * 'merge' the extensions, where 'extension' means one of two places that an extension can be defined
     * within a single catalog object (backend, service).
     *
     * (a) When called from CatalogHandler, the 'extensions' are the two levels where extension properties can
     * be defined within one catalog object. Either top-level, or 'x-vb'.
     *
     * (b) When called from ProtocolHandler, the extensions are the service or backend objects.
     * It is assumed that we have already resolved case (a), and are dealing with a flat extension object for
     * each catalog object.
     *
     * When merging different service catalog objects, we ONLY merge 'services' to 'backends', and we DO NOT
     * merge the 'services' headers. Backend extension are not merged to services here, though the caller can
     * do so.
     *
     * We also have to account for mutually-exclusive properties; keep a list of the closest property found,
     * and delete the rest at the end.
     *
     * Since the end of 19.3.1, the separation of 'backends' and 'services' is (mostly?) legacy, for deprecated syntax.
     * We merged services into backends, but not backends into services, to keep services headers as being for metadata
     * (swagger/openapi3) fetch only.
     * Since moving to the "paths" openapi3-snippet syntax to encapsulate anything specific to metadata fetching,
     * we could just merge all backends and services into one object (making things more uniform, etc).
     * But, for now, while in 'transition', continue to keep them separate here, and let the consumer merge services
     * to backends when appropriate.
     *
     * @param extensions
     * @returns {*}
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    mergeExtensions(...extensions) {
      // merge catalog object headers (those used to get the service definition (openapi).
      // first, remove any 'undefined' properties.
      extensions.forEach((extension) => {
        Object.keys(extension).forEach((prop) => {
          if (extension[prop] === undefined) {
            delete extension[prop]; // eslint-disable-line no-param-reassign
          }
        });
      });

      const headers = extensions.reduce((accum, current) => Object.assign(accum, (current && current.headers) || {}),
        {});

      // headers from extensions are combined; otherwise the second extensions overrides the first, etc.
      const merged = Object.assign({}, ...extensions, { headers });
      return merged;
    }

    /**
     * when merging services to backends, we don't want to merge everything
     * headers for service are used for loading the service def, headers for backends are for (backend) endpoint fetch.
     * @param referringObject  {url, extensions: {services: {}, backends: {}}, metadata: {}}
     *         the current catalog model object whose url refers to the object that needs merging.
     * @param sourceProperties source object for the merge; a 'services' or 'backends' extensions object,
     *          a selected subset of the referringObject.
     * @param sourceType for now, only 'services'
     * @param destType for now, only 'backends'
     * @returns {*}
     */
    // eslint-disable-next-line class-methods-use-this
    getPropertiesForCrossObjectMerge(referringObject, sourceProperties, sourceType, destType) {
      const newProps = Object.assign({}, sourceProperties);
      if (sourceType === ServiceConstants.ExtensionTypes.SERVICES &&
          destType === ServiceConstants.ExtensionTypes.BACKENDS) {
        // if we're using the new metadata syntax, do not prevent "services" headers from merging into "backends"
        // (don't delete the headers from the source "services" object) (bufp-31121)
        const newSyntax = referringObject && referringObject.metadata && referringObject.metadata.services &&
          referringObject.metadata.services.openapi;
        if (!newSyntax) {
          delete newProps.headers;
        }
      } else {
        // shouldn't happen
        logger.warn('unexpected source or destination types. source =', sourceType, 'destination =', destType);
      }
      return newProps;
    }

    /**
     * choose the proper server from the list.
     * Uses 'profile' if available, and return the first match. Otherwise, just use the first one.
     * @param servers
     * @returns {*}
     * @private
     */
    _findServer(servers = []) {
      const server = OpenApiUtils.findServerForProfile(servers, this.profile);
      logger.info('catalog handler using server with url', server.url, 'for profile', this.profile);
      return server;
    }

    /**
     * create an object that contains information about what we have resolved for the current object.
     *
     * This is merging the headers/transforms information from the current "service" or "backend" object,
     * with ALL the "server" extension information form the SAME object.
     * The server is chosen from the array using a profile, or [0].
     *
     * {
     *   url: resolved url,
     *   type: backends/services, unused
     *   name: id, unused
     *   extensions: {
     *     // contain either a 'services' or 'backends' property, depending on the type
     *     // the extension for the backends and services are not merged together here, but in ProtocolRegistry.
     *   }
     * }
     * @param catalogObject {object} current object in the chain (backend or server) we are following
     * @param serverObj {object} current server object on the chain
     * @param url {string}
     * @param catalogObjectType {string}
     * @param catalogObjectName {string}
     * @param namespace {string}
     * @returns {*}
     * @private
     */
    _createResolvedInfo(catalogObject, serverObj, url, catalogObjectType, catalogObjectName, namespace) {
      const rootTokenPath = TOKEN_VARIABLES_PATH.slice();
      rootTokenPath.push(...[catalogObjectType, catalogObjectName]);

      // look for services: { catalog: { backends: { myBackendId: { myVar } } }
      const rootTokens = rootTokenPath.reduce((accum, current) => accum[current] || {}, this.config) || {};

      // look services: { catalog: { common: { myVar } }
      const commonTokens =
        TOKEN_COMMON_VARIABLES_PATH.reduce((accum, current) => accum[current] || {}, this.config) || {};

      // now look for config['services.catalog.backends.myBackendId.XXX'] or config['services.catalog.common.XXX']
      const commonFlatTokens = {};
      const rootFlatTokens = {};
      const commonPrefix = TOKEN_COMMON_VARIABLES_PATH.join('.');
      const rootPrefix = rootTokenPath.join('.');
      Object.keys(this.config).forEach((name) => {
        if (name.startsWith(commonPrefix)) {
          commonFlatTokens[name.substring(commonPrefix.length + 1)] = this.config[name];
        }
        if (name.startsWith(rootPrefix)) {
          rootFlatTokens[name.substring(rootPrefix.length + 1)] = this.config[name];
        }
      });

      // now get the default values for the "variables"
      const defaults = {};
      Object.keys(serverObj.variables || {}).forEach((varName) => {
        if (serverObj.variables[varName].default) {
          defaults[varName] = serverObj.variables[varName].default;
        }
      });

      // .. then construct a one-level object, with flattened sub-object property names (level1.level2....)
      // AND the 'common' names
      const parameters = Object.assign(defaults,
        Utils.flatten(commonTokens),
        Utils.flatten(rootTokens),
        commonFlatTokens,
        rootFlatTokens);

      const uriTemplate = new UriTemplate(url || '', {}, true); // do not append extra values to url

      // 'combine' the headers and transforms of the base object and chosen server object

      // the new services object syntax allows extensions (headers, transforms) defined
      // in the (openapi3) "info.x-vb" section.
      // the old syntax used to just allow a "headers" property at the root. respect them both.
      // old/original:
      //   "services": "myService": { { "headers": {...}, "servers": [...], ...} }
      // new: "services":
      //   { "myServices": { "info": { "x-vb": { "headers": {...}, transforms: {...} } }, "servers": [...], ...}

      const infoExtensions = (catalogObject.info && catalogObject.info[ServiceConstants.VB_EXTENSIONS]) || {};

      // its possible to define 'headers' and 'transforms' outside of an 'x-vb', as top-level props
      // @todo: do we still support the 'top-level' headers/transforms, and should we deprecate these? they ARE used :(
      const topLevelExtensionsForMerge = {
        headers: catalogObject.headers,
        transforms: catalogObject.transforms,
      };

      /* precedence is low to high:
       * - top-level props from catalog object, which only allows "headers" and "transforms"
       * - "info.x-vb" object from the openapi3 fragment in the catalog object
       * - "x-vb" from "servers" array object
       */
      const mergedExtensions = this.mergeExtensions({},
        topLevelExtensionsForMerge,
        infoExtensions,
        serverObj[ServiceConstants.VB_EXTENSIONS] || {});

      const newServerObj = Object.assign({}, {
        namespace,
        type: catalogObjectType,
        name: catalogObjectName,
        url: uriTemplate.replace(parameters),
        extensions: {
          [catalogObjectType]: mergedExtensions,
        },
        metadata: {
          [catalogObjectType]: catalogObject.metadata,
        },
        serviceType: catalogObject.serviceType, // used by url mapping
      });

      return newServerObj;
    }

    /**
     * Get a list of named objects of both backends and services, so we can programmatically iterate resolutions of
     * vb-catalog urls.
     * THis includes all catalog obejcts, with enough information to resolve URLs.
     *
     * For example: for URL "vb-catalog://services/foo", if service "foo" refers to "vb-catalog://backends//bar",
     * the we could find the service "foo" and backend "bar" in the returned arrays, and resolve URLs.
     *
     * This was the API before the CatalogRegistry existed, and all access was abstracted
     * though the ProtocolRegistry.
     * As the role of Catalog is expanding, that abstraction is becoming awkward,
     * and clients may want to access the CatalogRegistry directly (via ConfigLoader).
     *
     * @todo: we make want to have the clients go directly to the CatalogRegistry in the future, and not through
     * the ProtocolRegistry.
     *
     * @see CatalogRegistry.getAllNames for an example
     *
     * @returns {Promise<Array<{services: Object[], backends: Object[], namespace: string}>>}
     */
    getNames() {
      return this.registry.getAllNames();
    }

    /*
     *
     * @param id catalogObject name
     * @param catalogObject
     * @param catalogPath
     * @returns {Promise<void>}
     */
    static getEndpointConfiguration(id, catalogObject, catalogPath) {
      return Promise.resolve()
        .then(() => {
          const op = OpenApiObjectFactory.get(catalogObject, { definitionUrl: catalogPath });
          const pathObject = op.pathObjects[0];
          if (!pathObject) {
            return null;
          }
          const pathKey = pathObject.name;
          const operationObjects = pathObject.getOperationObjects();
          const operationObject = operationObjects[0];
          if (!operationObject) {
            return null;
          }

          const operationKey = operationObject.name;

          // a stub service, to make sure name (used for proxy url, if needed) is put in the headers
          const service = Endpoint.createEmptyService(id);

          const params = {
            name: `_internal_${id}`, // unused, meaningless
            service,
            protocolRegistry: stubRegistry,
            pathKey,
            pathObject,
            operationKey,
            operationObject,
          };

          return new Endpoint(params).getConfig();
        });
    }
  }


  CatalogHandler.PROTOCOLS = [CommonConstants.VbProtocols.CATALOG];

  return CatalogHandler;
});

